import React from 'react';
import {
  FileOutlined,
  FileTextOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileZipOutlined,
  FileMarkdownOutlined,
  CodeOutlined,
} from '@ant-design/icons';

/**
 * Get the appropriate Ant Design icon for a file based on its extension
 * @param fileName - The name of the file including extension
 * @param className - Optional CSS class to apply to the icon
 * @returns React element with the appropriate file icon
 */
export const getFileIcon = (fileName: string, className?: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase() || '';

  // Document files
  if (['pdf'].includes(extension)) {
    return <FilePdfOutlined className={className} />;
  }

  if (['doc', 'docx', 'txt', 'rtf'].includes(extension)) {
    return <FileTextOutlined className={className} />;
  }

  // Spreadsheet files
  if (['xls', 'xlsx', 'csv'].includes(extension)) {
    return <FileExcelOutlined className={className} />;
  }

  // Image files
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico'].includes(extension)) {
    return <FileImageOutlined className={className} />;
  }

  // Archive files
  if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(extension)) {
    return <FileZipOutlined className={className} />;
  }

  // Markdown files
  if (['md', 'markdown'].includes(extension)) {
    return <FileMarkdownOutlined className={className} />;
  }

  // Code files
  if (
    [
      'js',
      'ts',
      'jsx',
      'tsx',
      'py',
      'java',
      'cpp',
      'c',
      'h',
      'cs',
      'go',
      'rb',
      'php',
      'swift',
      'kt',
      'rs',
      'scala',
      'sh',
      'bash',
    ].includes(extension)
  ) {
    return <CodeOutlined className={className} />;
  }

  // Data/config files
  if (['json', 'xml', 'yaml', 'yml', 'toml', 'ini', 'cfg', 'conf', 'env'].includes(extension)) {
    return <CodeOutlined className={className} />;
  }

  // Wolfram/Mathematica files
  if (['nb', 'wl', 'wls', 'm'].includes(extension)) {
    return <CodeOutlined className={className} />;
  }

  // Default icon for unknown types
  return <FileOutlined className={className} />;
};
